import React, { useState, useMemo, useEffect } from "react";
import { format, isValid, differenceInMinutes } from "date-fns";
import { getLoadRequestDetailsByID } from "apis/myRequestsService";
import { getTimeAgo } from "utils/dateUtils";
import { MyRequestResponse, MyRequestItem } from "pages/YourRequest/types";
import { useNavigate } from "react-router-dom";
import DefaultAirLogo from "../../assets/images/logo.svg";
import AirlineNotesModal from "components/AirlineNotesModal";
import { getAirlineNotesById, AirlineNotesResponse } from "apis/airlineNotesService";
import { AirlineNote, AirlineNoteSections } from "types/airlineNotes";
import { useToastStore } from "store/toastStore";

// Skeleton loader components
const SkeletonBox = ({ width = "100%", height = "1rem", className = "" }) => (
  <div className={`bg-gray-200 rounded animate-pulse ${className}`} style={{ width, height }} />
);

const FlightCardSkeleton = () => (
  <div className="mt-6 bg-white dark:bg-white p-5 rounded-lg w-full border-2 border-gray-100">
    <div className="grid grid-cols-12 gap-6 items-center justify-between">
      {/* Airline section */}
      <div className="flex items-center lg:col-span-2 md:col-span-4 col-span-12 gap-3">
        <SkeletonBox width="48px" height="48px" className="rounded" />
        <div className="text-left">
          <SkeletonBox width="60px" height="12px" className="mb-2" />
          <SkeletonBox width="120px" height="16px" />
        </div>
      </div>

      {/* Flight details section */}
      <div className="flex items-start lg:justify-normal md:justify-end lg:col-start-4 lg:col-span-4 md:col-span-8 col-span-12 gap-6">
        <div className="min-w-[100px]">
          <SkeletonBox width="70px" height="12px" className="mb-2" />
          <SkeletonBox width="80px" height="16px" />
        </div>
        <div className="min-w-[170px]">
          <SkeletonBox width="40px" height="12px" className="mb-2" />
          <SkeletonBox width="150px" height="16px" />
        </div>
        <div className="flex flex-col items-start">
          <SkeletonBox width="50px" height="12px" className="mb-2" />
          <SkeletonBox width="80px" height="28px" className="rounded-tr-lg rounded-bl-lg" />
        </div>
      </div>

      {/* Route section */}
      <div className="flex items-center lg:col-span-5 md:col-span-12 col-span-12 gap-3 justify-end">
        <div className="flex flex-col gap-1">
          <SkeletonBox width="60px" height="16px" />
          <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
            <SkeletonBox width="16px" height="16px" className="rounded-full" />
            <SkeletonBox width="40px" height="12px" />
          </div>
        </div>
        <div>
          <div className="flex items-center w-[250px]">
            <SkeletonBox width="12px" height="12px" className="rounded-full flex-shrink-0" />
            <SkeletonBox width="100%" height="2px" className="mx-2" />
            <SkeletonBox width="16px" height="16px" />
            <SkeletonBox width="100%" height="2px" className="mx-2" />
            <SkeletonBox width="12px" height="12px" className="rounded-full flex-shrink-0" />
          </div>
          <SkeletonBox width="60px" height="12px" className="mt-1 mx-auto" />
        </div>
        <div className="flex flex-col items-end gap-1">
          <SkeletonBox width="60px" height="16px" />
          <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
            <SkeletonBox width="16px" height="16px" className="rounded-full" />
            <SkeletonBox width="40px" height="12px" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AnsweredFlightSkeleton = () => (
  <div className="p-5 border-t border-gray-300 mt-3">
    <div className="grid grid-cols-12 gap-6 items-end justify-between">
      <div className="flex flex-col gap-1 lg:col-span-2 md:col-span-8 col-span-12">
        <SkeletonBox width="80px" height="12px" className="mb-1" />
        <SkeletonBox width="120px" height="16px" className="mb-2" />
        <div className="flex items-center gap-1">
          <SkeletonBox width="12px" height="12px" />
          <SkeletonBox width="60px" height="12px" />
        </div>
      </div>
      <div className="flex items-center lg:justify-normal md:justify-end lg:col-start-4 lg:col-span-5 md:col-span-5 col-span-12 gap-6">
        <div className="flex flex-col gap-2">
          <SkeletonBox width="80px" height="12px" />
          <div className="flex items-center gap-2">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="flex flex-col items-center border border-lightgray-100 py-1 w-12 rounded-md">
                <SkeletonBox width="20px" height="16px" className="mb-1" />
                <SkeletonBox width="30px" height="12px" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <SkeletonBox width="150px" height="12px" />
          <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-1 w-full rounded-md">
            <SkeletonBox width="20px" height="16px" className="mb-1" />
            <SkeletonBox width="30px" height="12px" />
          </div>
        </div>
      </div>
      <div className="lg:col-span-4 md:col-span-12 col-span-12 gap-3">
        <div className="flex items-center justify-between gap-4 border p-3 border-lightgray-100 rounded-md">
          <SkeletonBox width="80px" height="16px" />
          <SkeletonBox width="80px" height="24px" className="rounded-sm" />
        </div>
      </div>
    </div>
  </div>
);

// Define FlightDetails based on API response from getLoadRequestDetailsByID
interface FlightDetails {
  responderName: string;
  airlineName: string;
}

const transformNotesResponse = (data: AirlineNotesResponse, fallbackName: string): AirlineNote => {
  const sections: AirlineNoteSections = {};

  if (data.notes) {
    Object.entries(data.notes).forEach(([key, rawValue]) => {
      if (rawValue === null || rawValue === undefined) return;

      if (Array.isArray(rawValue)) {
        const cleanedItems = rawValue
          .map((item) => (typeof item === "string" ? item.trim() : ""))
          .filter((item): item is string => item.length > 0);

        if (cleanedItems.length > 0) {
          sections[key] = cleanedItems;
        }
        return;
      }

      if (typeof rawValue === "string") {
        const trimmed = rawValue.trim();
        if (trimmed.length > 0) {
          sections[key] = trimmed;
        }
      }
    });
  }

  return {
    id: data.id,
    airlineId: data.airlineId,
    airlineName: data.airlineName || fallbackName,
    sections,
    lastUpdated: data.createdAt,
    createdAt: data.createdAt,
  };
};

interface FlightListingProps {
  flights: MyRequestResponse;
  show?: boolean;
  FlightLoading?: boolean;
}

const statusColors: Record<string, string> = {
  Expired: "bg-red-600",
  "Request Updating": "bg-orange-500",
  Answered: "bg-green-500",
  Pending: "bg-yellow-500",
  default: "bg-gray-500",
};

const FlightListing: React.FC<FlightListingProps> = ({ flights, show, FlightLoading }) => {
  // console.log(flights)
  const showToast = useToastStore((state) => state.showToast);
  const [flightDataMap, setFlightDataMap] = useState<Record<number, FlightDetails>>({});
  const [isLoading, setIsLoading] = useState<Record<number, boolean>>({});
  const navigate = useNavigate();
  // const limitedFlights = useMemo(() => flights?.items?.slice(0, 5) || [], [flights]);
  const limitedFlights = useMemo(() => (Array.isArray(flights) ? flights.slice(0, 5) : []), [flights]);
  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedAirlineNote, setSelectedAirlineNote] = useState<AirlineNote | null>(null);
  const [notesCache, setNotesCache] = useState<Record<number, AirlineNote>>({});
  const [notesLoadingFlightId, setNotesLoadingFlightId] = useState<number | null>(null);
  // Fetch FlightDetails for Answered flights

  useEffect(() => {
    setIsAccordionOpen(!show); // Use show prop, default to false if undefined
  }, [show]);

  useEffect(() => {
    const answeredFlights = limitedFlights.filter((flight) => flight.status === "Answered");
    if (answeredFlights.length === 0) return;

    const fetchAnsweredFlightData = async () => {
      const newIsLoading = { ...isLoading };
      const newFlightDataMap = { ...flightDataMap };

      answeredFlights.forEach((flight) => {
        if (!flightDataMap[flight.id] && !isLoading[flight.id]) {
          newIsLoading[flight.id] = true;
        }
      });
      setIsLoading(newIsLoading);

      const promises = answeredFlights.map(async (flight) => {
        const id = flight.id;
        if (!flightDataMap[id] && !isLoading[id]) {
          try {
            const data = await getLoadRequestDetailsByID(id);
            return { id, data };
          } catch (error) {
            console.error(`Error fetching details for flight ${id}:`, error);
            return { id, data: null };
          }
        }
        return null;
      });

      const results = await Promise.all(promises);

      results.forEach((result) => {
        if (result) {
          // Only access result.id if result is not null
          if (result.data) {
            newFlightDataMap[result.id] = result.data;
          }
          newIsLoading[result.id] = false; // Safe: result is guaranteed to have id
        }
      });

      setFlightDataMap(newFlightDataMap);
      setIsLoading(newIsLoading);
    };

    fetchAnsweredFlightData();
  }, [limitedFlights]); // Remove flightDataMap and isLoading from dependencies

  const getFlightDuration = (departure: string, arrival: string): string => {
    const departureTime = new Date(departure);
    const arrivalTime = new Date(arrival);
    const durationMs = arrivalTime.getTime() - departureTime.getTime();
    const totalMinutes = Math.floor(durationMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const formatCreatedDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (!isValid(date)) return "";
      return format(date, "MMMM dd yyyy - EEE"); // 👈 dd for zero-padded day
    } catch {
      return "";
    }
  };

  const handleCardClick = () => {
    navigate("/my-loads");
  };

  // Handle opening airline notes modal
  const handleOpenNotes = async (flight: any, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Prevent card navigation

    const airlineId = flight?.airlineId;
    const flightId = flight?.id;
    if (!airlineId) {
      showToast("Airline information not available for this request.", "error", 3000);
      return;
    }

    if (!flightId) {
      showToast("Flight information not available for this request.", "error", 3000);
      return;
    }

    const cached = notesCache[airlineId];
    if (cached) {
      setSelectedAirlineNote(cached);
      setIsNotesModalOpen(true);
      return;
    }

    try {
      setNotesLoadingFlightId(flightId);
      const response = await getAirlineNotesById(airlineId);
      const transformed = transformNotesResponse(response, flight?.airlineName || "Unknown");
      setNotesCache((prev) => ({ ...prev, [airlineId]: transformed }));
      setSelectedAirlineNote(transformed);
      setIsNotesModalOpen(true);
    } catch (error) {
      console.error("Error fetching airline notes:", error);
      showToast("Unable to load airline notes. Please try again later.", "error", 3000);
    } finally {
      setNotesLoadingFlightId(null);
    }
  };

  // Handle closing airline notes modal
  const handleCloseNotes = () => {
    setIsNotesModalOpen(false);
    setSelectedAirlineNote(null);
  };

  return (
    <section className="mt-4 md:mt-6">
      <details open={isAccordionOpen} className="mt-4 md:mt-6 bg-white dark:bg-white p-3 md:p-5 rounded-lg w-full">
        <summary className="text-base md:text-lg font-semibold text-grease-100 mb-3 md:mb-4 cursor-pointer">
          Recently Posted Requests
        </summary>
        <div>
          {limitedFlights.length === 0 && !FlightLoading ? (
            <div className="flex justify-center items-center h-24 md:h-32 w-full">
              <p className="text-gray-500 text-sm md:text-base">No flights found.</p>
            </div>
          ) : FlightLoading ? (
            // Show skeleton loaders while flights are loading
            [...Array(3)].map((_, index) => <FlightCardSkeleton key={`skeleton-${index}`} />)
          ) : (
            limitedFlights.map((flight: any) => (
              <div
                key={flight.id}
                className="mt-4 md:mt-6 bg-white rounded-xl p-4 w-full border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all cursor-pointer"
                onClick={handleCardClick}
              >
                <div className="flex items-center gap-5">
                  {/* Airline Logo & Info */}
                  <div className="flex items-center gap-3 w-[180px] flex-shrink-0">
                    <img
                      className="w-10 h-10 object-contain rounded-full flex-shrink-0"
                      src={flight.airlineLogo || DefaultAirLogo}
                      alt={flightDataMap[flight.id]?.airlineName || "Airline"}
                      onError={(e) => (e.currentTarget.src = DefaultAirLogo)}
                    />
                    <div className="min-w-0">
                      <p className="text-grease-100 text-sm font-semibold leading-tight truncate">{flight.airlineName || "Unknown"}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{flight.flightNo}</p>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="w-[150px] flex-shrink-0">
                    <p className="text-gray-400 text-xs">Date</p>
                    <p className="text-grease-100 text-sm font-medium mt-0.5 whitespace-nowrap">
                      {formatCreatedDate(flight.departure)}
                    </p>
                  </div>

                  {/* Status */}
                  <div className="w-[100px] flex-shrink-0">
                    <p className="text-gray-400 text-xs">Status</p>
                    <p
                      className={`text-white text-xs py-1 px-3 mt-1 rounded-tr-lg rounded-bl-lg rounded-tl-none rounded-br-none w-fit ${
                        statusColors[flight.status] || statusColors.default
                      }`}
                    >
                      {flight.status}
                    </p>
                  </div>

                  {/* Airline Notes Button */}
                  <button
                    onClick={(e) => handleOpenNotes(flight, e)}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg transition group border border-[#FFBC00] border-opacity-30 flex-shrink-0 ${
                      notesLoadingFlightId === flight.id ? "bg-yellow-50 cursor-not-allowed" : "hover:bg-yellow-50"
                    }`}
                    aria-label="View airline notes"
                    title="View airline notes"
                    disabled={notesLoadingFlightId === flight.id}
                  >
                    {notesLoadingFlightId === flight.id ? (
                      <svg
                        className="w-5 h-5 text-[#FFBC00] animate-spin"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                    ) : (
                      <i className="st-info text-[#FFBC00] text-lg group-hover:text-yellow-600"></i>
                    )}
                  </button>

                  {/* Departure */}
                  <div className="flex flex-col items-center w-[80px] flex-shrink-0 ml-auto">
                    <p className="text-grease-100 text-base font-bold">{flight.sourceCode}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <i className="st-top-right text-gray-400 text-[8px]" />
                      <p className="text-gray-400 text-xs">
                        {flight.departure
                          ? new Date(flight.departure).toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                              timeZone: "UTC",
                            })
                          : ""}
                      </p>
                    </div>
                  </div>

                  {/* Center Path */}
                  <div className="flex flex-col items-center flex-1 min-w-[160px] px-2">
                    <div className="flex items-center w-full">
                      <div className="w-2 h-2 rounded-full border-2 border-light-yellow flex-shrink-0" />
                      <div className="flex-1 border-b-2 border-dashed border-gray-200 mx-1" />
                      <i className="st-flight-slim text-grease-100 text-sm mx-1" />
                      <div className="flex-1 border-b-2 border-dashed border-gray-200 mx-1" />
                      <div className="w-2 h-2 rounded-full border-2 border-light-yellow flex-shrink-0" />
                    </div>
                    <p className="text-gray-400 text-[10px] text-center mt-1">
                      {flight?.elptim && (
                        <span>
                          {flight.elptim.includes(":")
                            ? `${flight.elptim.split(":")[0]}h ${flight.elptim.split(":")[1]}m`
                            : `${parseInt(flight.elptim.slice(0, -2))}h ${parseInt(
                                flight.elptim.slice(-2)
                              )}m`}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Arrival */}
                  <div className="flex flex-col items-center w-[80px] flex-shrink-0">
                    <p className="text-grease-100 text-base font-bold">{flight.destinationCode}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <i className="st-bottom-right text-gray-400 text-[8px]" />
                      <p className="text-gray-400 text-xs">
                        {flight.arrival
                          ? new Date(flight.arrival).toLocaleTimeString("en-GB", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                              timeZone: "UTC",
                            })
                          : ""}
                      </p>
                    </div>
                  </div>

                </div>

                {isLoading[flight.id] ? (
                  <AnsweredFlightSkeleton />
                ) : (
                  flight.status === "Answered" && (
                    <div className="p-3 md:p-5 border-t border-gray-300 mt-3">
                      <div className="grid grid-cols-12 gap-3 md:gap-6 items-end justify-between">
                        <div className="flex flex-col gap-1 lg:col-span-2 md:col-span-8 col-span-12">
                          <p className="text-lightgray-200 text-xs">Answered By</p>
                          <p className="text-grease-100 text-sm font-medium">{flight?.responderName || "Unknown"}</p>
                          <div className="flex items-center gap-1 text-xs text-lightgray-150">
                            <i className="st-time"></i>
                            <p>{getTimeAgo(flight.modifiedDate)}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center lg:col-start-4 lg:col-span-5 md:col-span-5 col-span-12 gap-3 md:gap-6">
                          <div className="flex flex-col gap-2">
                            <p className="text-lightgray-200 text-xs">Open Seats</p>
                            <div className="flex items-center gap-1 md:gap-2">
                              <div className="flex flex-col items-center border border-lightgray-100 py-1 w-10 md:w-12 rounded-md">
                                <p className="text-grease-100 text-xs md:text-sm font-medium">{flight.firstClass || 0}</p>
                                <p className="text-lightgray-150 text-xs">First</p>
                              </div>
                              <div className="flex flex-col items-center border border-lightgray-100 py-1 w-10 md:w-12 rounded-md">
                                <p className="text-grease-100 text-xs md:text-sm font-medium">{flight.busClass || 0}</p>
                                <p className="text-lightgray-150 text-xs">Bus</p>
                              </div>
                              <div className="flex flex-col items-center border border-lightgray-100 py-1 w-10 md:w-12 rounded-md">
                                <p className="text-grease-100 text-xs md:text-sm font-medium">{flight.ecoPlus || 0}</p>
                                <p className="text-lightgray-150 text-xs">Eco+</p>
                              </div>
                              <div className="flex flex-col items-center border border-lightgray-100 py-1 w-10 md:w-12 rounded-md">
                                <p className="text-grease-100 text-xs md:text-sm font-medium">{flight.eco || 0}</p>
                                <p className="text-lightgray-150 text-xs">Eco</p>
                              </div>
                              <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-1 w-10 md:w-12 rounded-md">
                                <p className="text-grease-100 text-xs md:text-sm font-medium">{flight.total || 0}</p>
                                <p className="text-lightgray-150 text-xs">Total</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <p className="text-lightgray-200 text-xs">Listed Non-Rev Passengers</p>
                            <div className="flex flex-col items-center bg-lightgray-100 bg-opacity-40 py-1 w-full rounded-md">
                              <p className="text-grease-100 text-xs md:text-sm font-medium">{flight.nonRevListed || 0}</p>
                              <p className="text-lightgray-150 text-xs">Total</p>
                            </div>
                          </div>
                        </div>
                        <div className="lg:col-span-4 md:col-span-12 col-span-12 gap-3">
                          <div className="flex items-center justify-between gap-2 md:gap-4 border p-2 md:p-3 border-lightgray-100 rounded-md">
                            <p className="text-grease-100 text-sm font-medium">{flight.flightNo}</p>
                            <p className="text-white bg-green-500 rounded-sm px-1 py-0.5 text-xs uppercase">Scheduled</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            ))
          )}
        </div>
      </details>

      {/* Airline Notes Modal */}
      <AirlineNotesModal isOpen={isNotesModalOpen} onClose={handleCloseNotes} airlineNote={selectedAirlineNote} />
    </section>
  );
};

export default FlightListing;
